import { useCallback } from 'react'
import { useHistory, useQueryParams } from '@fs/zion-router'
import useViewMode from '../../../views/imageBrowse/hooks/useViewMode'
import { useGroupData, useImageOrganizeContext, useImageOrganizeModes } from '../../../views/imageBrowse/hooks'

/**
 * useKeepGroupIdInSync - This hook is used to keep the groupId in sync with the focusedIndex when in group or divide mode
 *    We use useHistory instead of useQuery because history is used to update the ark when focused index changes. When setQuery
 *    is used right after the ark changes, setQuery updates the url with the previous ark. We want to keep the new ark.
 * @returns
 */
export default function useKeepGroupIdInSync() {
  const history = useHistory()
  const { viewMode } = useViewMode()
  const { dividerOffsets } = useImageOrganizeContext()
  const { isDivideMode } = useImageOrganizeModes()
  const { groupIndex: originalGroupIndex, setGroupIndex, initialGroup } = useGroupData()
  const { query } = useQueryParams()

  function findChildIndexForNewFocusedIndex(x, array) {
    if (x <= array[0]) return 0
    if (x >= array[array.length - 1]) return array.length - 1
    return array.findIndex((value, index) => x >= value && x < array[index + 1])
  }

  const setGroupId = useCallback(
    (focusedIndex) => {
      if (
        (viewMode === 'group' || isDivideMode) &&
        dividerOffsets.length > 0 &&
        originalGroupIndex > -1 &&
        focusedIndex > -1
      ) {
        const newGroupIndex = findChildIndexForNewFocusedIndex(focusedIndex, dividerOffsets)
        if (newGroupIndex === -1 || newGroupIndex === originalGroupIndex) return

        setGroupIndex(newGroupIndex)
        const newGroupId = initialGroup?.childGroups?.[newGroupIndex]?.id
        if (newGroupId && newGroupId !== query.groupId) {
          const newSearch = window.location.search.replace(/&groupId=[^&]*/, `&groupId=${newGroupId}`)
          history.replace({
            pathname: window.location.pathname,
            search: newSearch,
          })
        }
      }
    },
    [dividerOffsets, history, initialGroup, isDivideMode, originalGroupIndex, query.groupId, setGroupIndex, viewMode]
  )
  return setGroupId
}
